import makeStyles from '@mui/styles/makeStyles';
import { appPalette } from 'theme/App.palette';
import { theme } from 'theme/App.theme';

const colors = appPalette.colors;
function focusableStyles(offset: number) {
  return {
    outline: `0.125rem solid`,
    outlineOffset: `.${offset}rem`,
  };
}

export const useBasicCardStyles = makeStyles({
  root: {
    border: `0.0625rem solid ${colors.light_grey_2}`,
    position: 'relative',
  },
  imgContainer: {
    display: 'flex',
    height: '0',
    paddingTop: '43.32%',
    position: 'relative',
  },
  img: {
    position: 'absolute',
    top: '0',
    height: '100%',
    width: 'auto',
    '&.default-img': {
      opacity: '.5',
    },
  },
  text: {
    height: '3.625rem',
    margin: '1.125rem 0.9375rem 0',
    [theme.breakpoints.up('viewportMedium')]: {
      height: '3.75rem',
    },
  },
  cardSkeleton: {
    '&.MuiSkeleton-root': {
      width: '100%',
      height: '9rem',
      [theme.breakpoints.up('viewportMobileMid')]: {
        height: '10.3125rem',
      },
      [theme.breakpoints.up('viewportMedium')]: {
        height: '9.5rem',
      },
      [theme.breakpoints.up('viewportLarge')]: {
        height: '12.875rem',
      },
      [theme.breakpoints.up('viewportXLarge')]: {
        height: '16.5rem',
      },
    },
  },
  basicCardWithCaptionSkeleton: {
    '&.MuiSkeleton-root': {
      width: '100%',
      height: '12.0625rem',
      [theme.breakpoints.up('viewportMobileMid')]: {
        height: '13.9375rem',
      },
      [theme.breakpoints.up('viewportMedium')]: {
        height: '12.6875rem',
      },
      [theme.breakpoints.up('viewportLarge')]: {
        height: '17.3125rem',
      },
      [theme.breakpoints.up('viewportXLarge')]: {
        height: '21.4375rem',
      },
    },
  },

  spinner_container: {
    backgroundColor: '#fff',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    zIndex: 2,
    opacity: 1,
    transition: 'opacity 0.5s ease',
  },

  spinner: {
    left: '50%',
    position: 'absolute',
    top: '50%',
    width: '4.375rem',
    height: '4.375rem',
    transform: 'translate(-50%, -50%)',
    '&:before': {
      animation: '$slide-spin .75s linear infinite',
      borderRadius: '50%',
      borderRight: '0.125rem solid transparent',
      borderTop: `0.125rem solid ${colors.acura_red}`,
      content: "''",
      height: '4.375rem',
      width: '4.375rem',
      position: 'absolute',
      boxSizing: 'border-box',
    },
  },
  modal_name: {
    paddingLeft: '0.3125rem',
  },
  '@keyframes slide-spin': {
    to: {
      transform: 'rotate(360deg)',
    },
  },
});

export const useComplexCardStyles = makeStyles({
  root: {
    maxWidth: '28.4375rem',
    boxShadow: `0 0.125rem 0.5rem ${colors.light_grey_1}`,
  },
  cardInfoContainer: {
    padding: '0.8125rem 1.25rem',
  },
  captionContainer: {
    padding: '1.25rem',
    paddingBottom: '2.5rem',
    flexDirection: 'column',
    '& span': {
      margin: '0 auto',
      paddingBottom: '0.3125rem',
    },
  },
  swiper_img: {
    width: '100%',
  },
  msrp: {
    textAlign: 'right',
  },
  swiper_navigation_button: {
    zIndex: 2,
    height: '1.3125rem !important',
    width: '0.625rem !important',
    [theme.breakpoints.up('viewportMedium')]: {
      height: '2.15625rem !important',
      width: '1.025rem !important',
    },
    [theme.breakpoints.up('viewportLarge')]: {
      height: '2.625rem !important',
      width: '1.25rem !important',
    },
    '&::after': {
      content: 'none !important',
    },
    '&::before': {
      content: '""',
      height: '3.75rem',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '5rem',
      [theme.breakpoints.up('viewportMedium')]: {
        height: '3.125rem',
        width: '4.375rem',
      },
      [theme.breakpoints.up('viewportLarge')]: {
        height: '6.25rem',
        width: '7.5rem',
      },
    },
    '& svg': {
      height: '100%',
      width: '100%',
    },
    '&:hover': {
      '& ~ .shadow': {
        opacity: 0.4,
      },
    },
    '&:focus': {
      'body:not(.acr-focus-disabled) &': {
        ...focusableStyles(2),
        outlineColor: colors.black,
      },
    },
  },
  swiper_navigation_full_toggle: {
    visibility: 'hidden',
    [theme.breakpoints.up('viewportLarge')]: {
      visibility: 'visible',
    },
  },
  swiper_navigation_button_prev: {
    left: '0.375rem !important',
    [theme.breakpoints.up('viewportMedium')]: {
      left: '0.63125rem !important',
    },
    [theme.breakpoints.up('viewportLarge')]: {
      left: '1rem !important',
    },
  },
  swiper_navigation_button_next: {
    right: '0.375rem !important',
    [theme.breakpoints.up('viewportMedium')]: {
      right: '0.63125rem !important',
    },
    [theme.breakpoints.up('viewportLarge')]: {
      right: '1rem !important',
    },
  },
  swiper_controls: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    padding: '0.625rem ',
    position: 'relative',
    textAlign: 'center',
    width: '100%',
    zIndex: 1,
  },
  swiperContainer: {
    minHeight: '12.125rem',
    [theme.breakpoints.up('viewportMobileMid')]: {
      minHeight: '17.1875rem',
    },
    [theme.breakpoints.up('viewportMedium')]: {
      minHeight: '14.875rem',
    },
    [theme.breakpoints.up('viewportLarge')]: {
      minHeight: '17rem',
    },
    [theme.breakpoints.up('viewportXLarge')]: {
      minHeight: '17.1875rem',
    },
  },
  swiper: {
    '& .swiper-pagination': {
      display: 'inline-flex !important',
      minHeight: '1.25rem',
      bottom: '1.25rem !important',
      position: 'relative',
      '& .swiper-pagination-bullet': {
        background: colors.light_grey_1,
        height: '0.25rem',
        width: '0.25rem',
        [theme.breakpoints.up('viewportMedium')]: {
          height: '0.4375rem',
          width: '0.4375rem',
        },
        borderRadius: '50%',
        margin: 'auto 8px',
        verticalAlign: 'middle',
        '&.active': {
          background: colors.black,
          height: '0.375rem',
          width: '0.375rem',
          [theme.breakpoints.up('viewportMedium')]: {
            height: '0.6875rem',
            width: '0.6875rem',
          },
        },
        '&:focus': {
          'body:not(.acr-focus-disabled) &': {
            ...focusableStyles(2),
            outlineColor: colors.black,
          },
        },
      },
    },
  },
  colorsContainer: {
    borderTop: `1px solid ${colors.light_grey_3}`,
    borderBottom: `1px solid ${colors.light_grey_3}`,
    padding: '0.625rem 0',
  },
  modelContainer: {
    padding: ' 0.3125rem 0',
  },
  colorImg: {
    borderRadius: '50%',
    height: '1.875rem',
    width: '1.875rem',
  },
  colorText: {
    padding: '0 0.625rem',
  },
  colorItem: {
    display: 'flex',
    alignItems: 'center',
  },
  bullets: {
    padding: '1.25rem 0',
  },
  bullet: {
    display: 'block',
    '&:before': {
      backgroundColor: colors.light_grey_6,
      borderRadius: '50%',
      content: "''",
      display: 'inline-block',
      height: '0.375rem',
      marginRight: '0.3125rem',
      verticalAlign: 'middle',
      width: '0.375rem',
    },
  },
  cardSkeleton: {
    '&.MuiSkeleton-root': {
      width: '100%',
      maxWidth: '28.4375rem',
      height: '12.125rem',
      [theme.breakpoints.up('viewportMobileMid')]: {
        height: '17.1875rem',
      },
      [theme.breakpoints.up('viewportMedium')]: {
        height: '14.875rem',
      },
      [theme.breakpoints.up('viewportLarge')]: {
        height: '17rem',
      },
      [theme.breakpoints.up('viewportXLarge')]: {
        height: '17.1875rem',
      },
    },
  },
});
